import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { LayoutGrid, ChevronLeft, ChevronRight, Loader2, Search, X } from "lucide-react";
import { api } from "@/lib/api";
import { BidCard } from "@/components/BidCard";

// Global aggregated directory (rank 4 onwards). Paginated with 20 / 50 rows per page,
// with a sticky real-time search over brand name / keyword / location.
export function GlobalDirectory({ region = "global" }) {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const currency = region === "MY" ? "RM " : "$";
  const boardPrefix = region === "MY" ? "/my/k/" : "/k/";

  // Debounce the search input to filter in near real-time without hammering the API.
  useEffect(() => {
    const t = setTimeout(() => { setDebouncedQuery(query.trim()); setPage(1); }, 300);
    return () => clearTimeout(t);
  }, [query]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/bids/global-leaderboard", {
        params: { region, page, limit, min_rank: 4, q: debouncedQuery },
      });
      setData(res.data);
    } catch {
      setData(null);
    }
    setLoading(false);
  }, [region, page, limit, debouncedQuery]);

  useEffect(() => { load(); }, [load]);

  const setPerPage = (n) => { setLimit(n); setPage(1); };
  const searching = debouncedQuery.length > 0;

  // Hide the whole section only when there's nothing to show AND no active search.
  if (!loading && !searching && (!data || data.total === 0)) return null;

  return (
    <section className="max-w-6xl mx-auto px-4 pb-32" data-testid="global-directory">
      <div className="flex items-center gap-2 mb-4">
        <LayoutGrid className="w-5 h-5 text-blue-400" />
        <h2 className="font-display font-bold text-xl tracking-tight">The Directory</h2>
        {data && <span className="text-xs text-white/40">{data.total} businesses</span>}
      </div>

      {/* Sticky search + rows-per-page controls */}
      <div className="sticky top-16 z-20 -mx-4 px-4 py-3 mb-5 glass-strong rounded-2xl flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex-1 flex items-center gap-3 bg-white/5 rounded-xl px-4 h-11 focus-within:ring-2 focus-within:ring-blue-500/40 transition-all">
          <Search className="w-4 h-4 text-white/40 shrink-0" />
          <input
            data-testid="directory-search-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search businesses, keywords or locations…"
            className="flex-1 bg-transparent outline-none text-sm placeholder:text-white/30"
          />
          {query && (
            <button data-testid="directory-search-clear" onClick={() => setQuery("")} className="text-white/40 hover:text-white transition-colors">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        <div className="flex items-center gap-2 text-xs shrink-0">
          <span className="text-white/40">Rows:</span>
          {[20, 50].map((n) => (
            <button
              key={n}
              data-testid={`directory-perpage-${n}`}
              onClick={() => setPerPage(n)}
              className={`rounded-full px-3 py-1 border transition-colors ${limit === n ? "border-blue-500/50 bg-blue-500/10 text-blue-300" : "border-white/10 text-white/50 hover:text-white/80"}`}
            >
              {n}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-white/40" /></div>
      ) : data && data.total === 0 ? (
        <div data-testid="directory-empty" className="glass rounded-2xl p-10 text-center text-white/40">
          No businesses match "{debouncedQuery}".
        </div>
      ) : (
        <>
          <div className="space-y-3" data-testid="directory-rows">
            {data.entries.map((e) => (
              <button
                key={`${e.slug}-${e.user_id}`}
                onClick={() => navigate(`${boardPrefix}${e.slug}`)}
                className="w-full text-left"
                data-testid={`directory-row-${e.global_rank}`}
              >
                <BidCard entry={{ ...e, rank: e.global_rank }} currency={currency} showPhone={true} />
                <div className="mt-1 ml-11 sm:ml-16 text-[11px] text-white/30">on /{e.slug}</div>
              </button>
            ))}
          </div>

          {data.total_pages > 1 && (
            <div className="flex items-center justify-center gap-4 mt-8">
              <button
                data-testid="directory-prev"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="inline-flex items-center gap-1 rounded-full glass px-4 py-2 text-sm disabled:opacity-30 hover:border-white/25 transition-colors"
              >
                <ChevronLeft className="w-4 h-4" /> Prev
              </button>
              <span className="text-sm text-white/50 tabular-nums">Page {page} / {data.total_pages}</span>
              <button
                data-testid="directory-next"
                disabled={page >= data.total_pages}
                onClick={() => setPage((p) => Math.min(data.total_pages, p + 1))}
                className="inline-flex items-center gap-1 rounded-full glass px-4 py-2 text-sm disabled:opacity-30 hover:border-white/25 transition-colors"
              >
                Next <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </>
      )}
    </section>
  );
}
